import { useCallback, useEffect, useState } from 'react';

import styles from './CartPayButton.module.scss';

import { useAuth } from '@/app/providers/AuthContext';
import { companyStore } from '@/entities/company/store/companyStore';
import { orderStore } from '@/entities/order/store/orderStore';
import { restaurantStore } from '@/entities/restaurant/store/restaurantStore';
import { useWorkTime } from '@/features/restaurants/lib/useWorkTime';
import { STORAGE_KEYS } from '@/shared/constants/storageKeys';
import { formatPrice } from '@/shared/lib/currency';
import { Button } from '@/shared/ui-kit/Button/Button';
import { Checkbox } from '@/shared/ui-kit/Checkbox/Checkbox';
import { Icon } from '@/shared/ui-kit/Icon';
import { useToast } from '@/shared/ui-kit/ToastProvider/useToast';

type Props = { total: number };

export const CartPayButton: React.FC<Props> = ({ total }: Props) => {
  const { isLoggedIn, setAuthModalVisible } = useAuth();
  const { showError } = useToast();
  const [agreed, setAgreed] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const create = useCallback(async () => {
    const restaurantId = restaurantStore.current.value?.id ?? null;
    if (restaurantId == null) {
      showError('Не выбран ресторан');
      return;
    }

    try {
      const pay = await orderStore.create(restaurantId);
      if (!pay?.payment_url) {
        showError('Не удалось получить адрес оплаты');
        return;
      }

      localStorage.setItem('lastOrderId', String(pay.order_id));
      localStorage.setItem('lastRestaurantId', String(restaurantId));

      window.location.href = pay.payment_url;
    } catch {
      showError('Ошибка создания заказа');
    }
  }, [showError]);

  const handleClick = useCallback(() => {
    if (!agreed) {
      setSubmitted(true);
      showError('Отметьте согласие с условиями');
      return;
    }
    if (!isLoggedIn) {
      localStorage.setItem(
        STORAGE_KEYS.postAuthAction,
        STORAGE_KEYS.postAuthCheckout
      );
      setAuthModalVisible(true);
      return;
    }
    create().catch(() => {});
  }, [isLoggedIn, setAuthModalVisible, create, agreed, showError]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const action = localStorage.getItem(STORAGE_KEYS.postAuthAction);
    if (action !== STORAGE_KEYS.postAuthCheckout) return;
    localStorage.removeItem(STORAGE_KEYS.postAuthAction);
    create().catch(() => {});
  }, [isLoggedIn, create]);

  const isBusy = orderStore.loading.value;
  const { opened, text } = useWorkTime();

  return (
    <div className={styles.container}>
      <div className={styles.checkbox}>
        <Checkbox
          error={submitted && !agreed}
          checked={agreed}
          classNames={{ label: styles.checkboxLabel }}
          onChange={setAgreed}
        >
          Согласен на обработку персональных данных (
          <a
            href={companyStore.agreementDocument.value?.file ?? '#'}
            target="_blank"
            rel="noreferrer"
          >
            1
          </a>
          ) в соответствии с Политикой обработки персональных данных (
          <a
            href="/documents/Политика_обработки_персональных_данных_для_АДВ_на_сайт_ADV.pdf"
            target="_blank"
            rel="noreferrer"
          >
            2
          </a>
          )
        </Checkbox>
      </div>
      <div className={styles.divider} />
      <Button
        className={styles.checkout}
        onClick={handleClick}
        disabled={isBusy || !opened}
      >
        {isBusy ? 'Создаём заказ…' : `К оплате ${formatPrice(total)}`}
      </Button>
      {opened ? (
        <span className={styles.subLabel}>начнем готовить после оплаты</span>
      ) : (
        <span className={styles.closed}>
          {text}
          <Icon name="outlinedMoon" width={16} height={16} />
        </span>
      )}
    </div>
  );
};
